import type { ReactNode } from "react";
import type { Student } from "@/types/assessment";

const EMPTY_PRINT_VALUE = "\u00a0";

export function PrintableReportDocument({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <article aria-label={ariaLabel} className="report-print-document">
      {children}
    </article>
  );
}

export function PrintableReportHeader({
  eyebrow,
  title,
  description,
  student,
  assessmentLabel,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  student: Student | undefined;
  assessmentLabel: string;
}): JSX.Element {
  return (
    <header className="report-print-cover report-print-block">
      <div className="flex items-start justify-between gap-6 border-b-2 border-brand pb-4">
        <div>
          <p className="text-[10pt] font-semibold tracking-[0.14em] text-brand-dark">
            Child+智慧平台
          </p>
          <p className="mt-3 text-[10pt] font-semibold tracking-[0.12em] text-accent-cool">
            {eyebrow}
          </p>
          <h1 className="mt-1 text-[23pt] font-semibold leading-tight text-slate-900">{title}</h1>
          {description ? (
            <p className="mt-2 text-[11pt] leading-6 text-slate-600">{description}</p>
          ) : null}
        </div>
        <div aria-hidden="true" className="mt-1 h-12 w-12 rounded-full bg-brand/10 p-2">
          <div className="flex h-full w-full items-center justify-center rounded-full bg-brand text-[16pt] font-semibold text-white">
            {student?.profile.name.trim().slice(0, 1) || "+"}
          </div>
        </div>
      </div>

      <dl className="report-print-meta-grid mt-5 grid grid-cols-2 gap-x-8 gap-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-[10.5pt]">
        <PrintMeta label="儿童" value={student?.profile.name} />
        <PrintMeta label="性别 / 年龄" value={formatGenderAndAge(student)} />
        <PrintMeta label="学校 / 班级" value={formatEnrollment(student)} />
        <PrintMeta label="评估依据" value={assessmentLabel} />
      </dl>
    </header>
  );
}

export function PrintableReportSection({
  title,
  subtitle,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={`report-print-section ${className}`}>
      <div className="report-print-section-heading">
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function PrintableLineChart({
  ariaLabel,
  labels,
  values,
  max,
  valueSuffix = "",
}: {
  ariaLabel: string;
  labels: string[];
  values: number[];
  max?: number;
  valueSuffix?: string;
}): JSX.Element {
  const width = 720;
  const height = 240;
  const padding = { top: 24, right: 32, bottom: 50, left: 54 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const highestValue = Math.max(...values, 1);
  const yMax = Math.max(max ?? Math.ceil(highestValue * 1.15), 1);
  const pointCount = Math.max(values.length, 1);
  const points = values.map((value, index) => {
    const x =
      pointCount === 1
        ? padding.left + chartWidth / 2
        : padding.left + (chartWidth * index) / (pointCount - 1);
    const y = padding.top + chartHeight - (Math.max(0, value) / yMax) * chartHeight;
    return { x, y, value, label: labels[index] ?? "" };
  });
  const polyline = points.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      className="h-auto w-full"
    >
      <title>{ariaLabel}</title>
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = padding.top + chartHeight * ratio;
        const value = yMax * (1 - ratio);
        return (
          <g key={ratio}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y}
              y2={y}
              stroke="#dbe4d2"
              strokeWidth="1"
            />
            <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#64748b">
              {Number.isInteger(value) ? value : value.toFixed(1)}
            </text>
          </g>
        );
      })}
      {points.length > 1 ? (
        <polyline
          points={polyline}
          fill="none"
          stroke="#527a2a"
          strokeWidth="4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : null}
      {points.map((point, index) => (
        <g key={`${point.label}-${index}`}>
          <circle cx={point.x} cy={point.y} r="6" fill="#ffffff" stroke="#527a2a" strokeWidth="4" />
          <text
            x={point.x}
            y={point.y - 13}
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill="#334155"
          >
            {formatChartValue(point.value)}
            {valueSuffix}
          </text>
          <text x={point.x} y={height - 17} textAnchor="middle" fontSize="11" fill="#64748b">
            {point.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

export function PrintableRadarChart({
  ariaLabel,
  labels,
  values,
  maxValue = 100,
  compact = false,
}: {
  ariaLabel: string;
  labels: string[];
  values: number[];
  maxValue?: number;
  compact?: boolean;
}): JSX.Element {
  const width = 680;
  const height = compact ? 250 : 330;
  const centerX = width / 2;
  const centerY = height / 2 + (compact ? 2 : 4);
  const radius = compact ? 82 : 118;
  const count = Math.max(labels.length, 3);
  const safeMaxValue = Math.max(maxValue, 1);
  const valueScale = (value: number) => Math.max(0, Math.min(value, safeMaxValue)) / safeMaxValue;
  const pointAt = (index: number, scale: number) => {
    const angle = -Math.PI / 2 + (index * Math.PI * 2) / count;
    return {
      x: centerX + Math.cos(angle) * radius * scale,
      y: centerY + Math.sin(angle) * radius * scale,
    };
  };
  const polygon = (scale: number) =>
    labels
      .map((_, index) => pointAt(index, scale))
      .map((point) => `${point.x},${point.y}`)
      .join(" ");
  const valuePolygon = values
    .map((value, index) => pointAt(index, valueScale(value)))
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      className={`mx-auto h-auto w-full ${compact ? "max-w-[34rem]" : "max-w-[42rem]"}`}
    >
      <title>{ariaLabel}</title>
      {[0.25, 0.5, 0.75, 1].map((scale) => (
        <polygon
          key={scale}
          points={polygon(scale)}
          fill={scale === 1 ? "#f7faf4" : "none"}
          stroke="#dbe4d2"
          strokeWidth="1"
        />
      ))}
      {labels.map((label, index) => {
        const axis = pointAt(index, 1);
        const text = pointAt(index, compact ? 1.32 : 1.22);
        return (
          <g key={label}>
            <line
              x1={centerX}
              y1={centerY}
              x2={axis.x}
              y2={axis.y}
              stroke="#dbe4d2"
              strokeWidth="1"
            />
            <text x={text.x} y={text.y + 4} textAnchor="middle" fontSize="12" fill="#475569">
              {label}
            </text>
          </g>
        );
      })}
      <polygon
        points={valuePolygon}
        fill="#527a2a"
        fillOpacity="0.18"
        stroke="#527a2a"
        strokeWidth="3"
      />
      {values.map((value, index) => {
        const point = pointAt(index, valueScale(value));
        return (
          <circle
            key={`${labels[index]}-${value}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#527a2a"
          />
        );
      })}
    </svg>
  );
}

function PrintMeta({ label, value }: { label: string; value: string | undefined }): JSX.Element {
  return (
    <div>
      <dt className="inline font-semibold text-slate-600">{label}：</dt>
      <dd className="inline whitespace-pre-wrap break-words text-slate-800">
        {value?.trim() || EMPTY_PRINT_VALUE}
      </dd>
    </div>
  );
}

function formatGenderAndAge(student: Student | undefined): string {
  const gender = formatGender(student?.profile.gender);
  const age = student?.profile.age == null ? "" : `${student.profile.age} 岁`;
  return [gender, age].filter(Boolean).join(" / ");
}

function formatGender(gender: Student["profile"]["gender"] | undefined): string {
  if (gender === "male") return "男";
  if (gender === "female") return "女";
  if (gender === "other") return "其他";
  return "";
}

function formatEnrollment(student: Student | undefined): string {
  if (!student) return "";
  return [student.enrollment.school, student.enrollment.classroom].filter(Boolean).join(" / ");
}

function formatChartValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
